import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft, Plus, Trash2, Edit2, Save, X, Search, 
  ChevronLeft, ChevronRight, Target, Check, ChevronsUpDown, Eye, Move, GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { cn } from '../lib/utils';

const HotspotEditor = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const imageRef = useRef(null);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hotspots, setHotspots] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [dragMode, setDragMode] = useState(false);
  const [newHotspot, setNewHotspot] = useState(null);
  const [showMaterialSearch, setShowMaterialSearch] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [materialSearch, setMaterialSearch] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draggingHotspot, setDraggingHotspot] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [multiMode, setMultiMode] = useState(false);
  const [pendingHotspots, setPendingHotspots] = useState([]);
  const [currentGroup, setCurrentGroup] = useState('');
  const [groups, setGroups] = useState([]);

  const DEFAULT_GROUPS = ['Kitchen', 'Bathroom', 'Living Room', 'Bedroom', 'Outdoor', 'Hallway', 'Office'];

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/project-inspiration');
      return;
    }
    fetchProject();
    fetchMaterials();
  }, [projectId, user]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/project-inspiration/${projectId}`);
      setProject(response.data);
      setHotspots(response.data.material_hotspots || []);
      
      // Extract existing groups
      const existingGroups = [...new Set(
        (response.data.material_hotspots || [])
          .map(h => h.group_name)
          .filter(Boolean)
      )];
      setGroups([...new Set([...DEFAULT_GROUPS, ...existingGroups])]);
    } catch (error) {
      toast.error('Project not found');
      navigate('/project-inspiration');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials?limit=100`);
      setMaterials(response.data || []);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
    }
  };

  const filteredMaterials = materials.filter(m =>
    m.name.toLowerCase().includes(materialSearch.toLowerCase()) ||
    m.stone_type?.toLowerCase().includes(materialSearch.toLowerCase())
  );

  const currentImageHotspots = hotspots.filter(h => h.image_index === currentImageIndex);

  const handleImageClick = (e) => {
    if (dragMode || draggingHotspot) return;
    if (!editMode || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newPoint = {
      image_index: currentImageIndex,
      x_percent: Math.round(x * 10) / 10,
      y_percent: Math.round(y * 10) / 10,
      material_id: null,
      label: '',
      group_name: currentGroup || null
    };

    if (multiMode) {
      // In multi-mode, add to pending list
      setPendingHotspots([...pendingHotspots, newPoint]);
    } else {
      // Single mode - open material search
      setNewHotspot(newPoint);
      setShowMaterialSearch(true);
    }
  };

  const saveMultipleHotspots = async () => {
    if (!selectedMaterial || pendingHotspots.length === 0) {
      toast.error('Select a material and place at least one hotspot');
      return;
    }

    setSaving(true);
    try {
      const hotspotsToCreate = pendingHotspots.map(h => ({
        ...h,
        material_id: selectedMaterial.material_id,
        label: selectedMaterial.name,
        group_name: currentGroup || null
      }));

      const response = await axios.post(
        `${API}/project-inspiration/${projectId}/hotspots/batch`,
        { hotspots: hotspotsToCreate },
        { withCredentials: true }
      );

      // Add new hotspots to state
      setHotspots([...hotspots, ...response.data.added]);
      setPendingHotspots([]);
      setSelectedMaterial(null);
      toast.success(`Added ${response.data.added.length} hotspots!`);
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} hotspots failed`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add hotspots');
    } finally {
      setSaving(false);
    }
  };

  const clearPendingHotspots = () => {
    setPendingHotspots([]);
    setSelectedMaterial(null);
  };

  // Drag handlers
  const handleDragStart = (e, hotspot) => {
    if (!dragMode) return;
    e.preventDefault();
    e.stopPropagation();
    
    const rect = imageRef.current.getBoundingClientRect();
    setDraggingHotspot(hotspot);
    setDragOffset({
      x: e.clientX - (rect.left + (hotspot.x_percent / 100) * rect.width),
      y: e.clientY - (rect.top + (hotspot.y_percent / 100) * rect.height)
    });
  };

  const handleDragMove = (e) => {
    if (!draggingHotspot || !imageRef.current) return;
    e.preventDefault();
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - dragOffset.x - rect.left) / rect.width) * 100;
    const y = ((e.clientY - dragOffset.y - rect.top) / rect.height) * 100;
    
    // Clamp values between 0 and 100
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));
    
    // Update hotspot position in state
    setHotspots(hotspots.map(h => 
      h.hotspot_id === draggingHotspot.hotspot_id 
        ? { ...h, x_percent: clampedX, y_percent: clampedY }
        : h
    ));
  };

  const handleDragEnd = async () => {
    if (!draggingHotspot) return;
    
    const hotspot = hotspots.find(h => h.hotspot_id === draggingHotspot.hotspot_id);
    if (!hotspot) {
      setDraggingHotspot(null);
      return;
    }
    
    // Save new position to backend
    try {
      await axios.put(
        `${API}/project-inspiration/${projectId}/hotspots/${hotspot.hotspot_id}`,
        {
          x_percent: Math.round(hotspot.x_percent * 10) / 10,
          y_percent: Math.round(hotspot.y_percent * 10) / 10
        },
        { withCredentials: true }
      );
      toast.success('Hotspot repositioned');
    } catch (error) {
      toast.error('Failed to save position');
      // Revert on error
      fetchProject();
    }
    
    setDraggingHotspot(null);
  };

  const saveHotspot = async () => {
    if (!newHotspot || !selectedMaterial) {
      toast.error('Please select a material');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/project-inspiration/${projectId}/hotspots`,
        {
          image_index: newHotspot.image_index,
          x_percent: newHotspot.x_percent,
          y_percent: newHotspot.y_percent,
          material_id: selectedMaterial.material_id,
          label: newHotspot.label || selectedMaterial.name,
          group_name: currentGroup || null
        },
        { withCredentials: true }
      );

      const newHotspotData = {
        ...response.data.hotspot,
        material: response.data.material
      };
      setHotspots([...hotspots, newHotspotData]);
      setNewHotspot(null);
      setSelectedMaterial(null);
      setShowMaterialSearch(false);
      toast.success('Hotspot added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add hotspot');
    } finally {
      setSaving(false);
    }
  };

  const deleteHotspot = async (hotspotId) => {
    if (!confirm('Delete this hotspot?')) return;

    try {
      await axios.delete(
        `${API}/project-inspiration/${projectId}/hotspots/${hotspotId}`,
        { withCredentials: true }
      );
      setHotspots(hotspots.filter(h => h.hotspot_id !== hotspotId));
      toast.success('Hotspot deleted');
    } catch (error) {
      toast.error('Failed to delete hotspot');
    }
  };

  const cancelNewHotspot = () => {
    setNewHotspot(null);
    setSelectedMaterial(null);
    setShowMaterialSearch(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-stone-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <Header />
      
      <main className="flex-1 pt-20">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link 
                to={`/project-inspiration/${projectId}`}
                className="p-2 hover:bg-stone-200 rounded transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-medium text-stone-900">Edit Hotspots</h1>
                <p className="text-stone-500">{project.title}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Link to={`/project-inspiration/${projectId}`}>
                <Button variant="outline" className="gap-2">
                  <Eye className="w-4 h-4" /> Preview
                </Button>
              </Link>
              <Button
                onClick={() => { setDragMode(!dragMode); setEditMode(false); setMultiMode(false); }}
                variant={dragMode ? "default" : "outline"}
                className={dragMode ? 'bg-[var(--gold)] hover:bg-[var(--gold)]/80' : ''}
                data-testid="reposition-hotspots-btn"
              >
                <Move className="w-4 h-4 mr-2" /> {dragMode ? 'Dragging...' : 'Reposition'}
              </Button>
              <Button
                onClick={() => { setEditMode(!editMode); setDragMode(false); setMultiMode(false); }}
                className={editMode && !multiMode ? 'bg-green-600 hover:bg-green-700' : 'bg-stone-900 hover:bg-stone-800'}
                data-testid="add-hotspots-btn"
              >
                {editMode && !multiMode ? (
                  <>
                    <Target className="w-4 h-4 mr-2" /> Click to place
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" /> Add Single
                  </>
                )}
              </Button>
              <Button
                onClick={() => { setMultiMode(!multiMode); setEditMode(true); setDragMode(false); }}
                className={multiMode ? 'bg-purple-600 hover:bg-purple-700' : ''}
                variant={multiMode ? "default" : "outline"}
                data-testid="multi-hotspot-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Multi ({pendingHotspots.length})
              </Button>
            </div>
          </div>

          {/* Group Selector & Multi-mode controls */}
          {editMode && (
            <div className="flex flex-wrap items-center gap-4 mb-4 p-4 bg-stone-100 rounded">
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Room/Area:</Label>
                <select
                  value={currentGroup}
                  onChange={(e) => setCurrentGroup(e.target.value)}
                  className="px-3 py-1.5 border border-stone-300 rounded text-sm"
                >
                  <option value="">No Group</option>
                  {groups.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              
              {multiMode && (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-stone-600">
                      {pendingHotspots.length} point{pendingHotspots.length !== 1 ? 's' : ''} placed
                    </span>
                  </div>
                  
                  {pendingHotspots.length > 0 && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={clearPendingHotspots}
                      >
                        <X className="w-4 h-4 mr-1" /> Clear
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setShowMaterialSearch(true)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Select Material & Save
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Image Editor */}
            <div className="lg:col-span-2 space-y-4">
              <div 
                className={cn(
                  "relative bg-stone-200 overflow-hidden select-none",
                  editMode && "cursor-crosshair",
                  dragMode && "cursor-move"
                )}
                onMouseMove={dragMode ? handleDragMove : undefined}
                onMouseUp={dragMode ? handleDragEnd : undefined}
                onMouseLeave={dragMode ? handleDragEnd : undefined}
              >
                <img
                  ref={imageRef}
                  src={project.images?.[currentImageIndex]}
                  alt={project.title}
                  className="w-full aspect-[4/3] object-cover pointer-events-none"
                  draggable={false}
                />
                <div 
                  className="absolute inset-0"
                  onClick={handleImageClick}
                >
                  {/* Existing Hotspots */}
                  {currentImageHotspots.map((hotspot) => (
                    <div
                      key={hotspot.hotspot_id}
                      className={cn(
                        "absolute group",
                        dragMode && "cursor-grab",
                        draggingHotspot?.hotspot_id === hotspot.hotspot_id && "cursor-grabbing z-50"
                      )}
                      style={{ 
                        left: `${hotspot.x_percent}%`, 
                        top: `${hotspot.y_percent}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                      onMouseDown={(e) => handleDragStart(e, hotspot)}
                    >
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-all",
                        draggingHotspot?.hotspot_id === hotspot.hotspot_id 
                          ? "bg-[var(--gold)] border-2 border-white scale-125"
                          : dragMode 
                            ? "bg-[var(--gold)]/20 border-2 border-[var(--gold)] hover:scale-110"
                            : "bg-white border-2 border-stone-900"
                      )}>
                        {dragMode ? (
                          <GripVertical className="w-4 h-4 text-[var(--gold)]" />
                        ) : (
                          <Plus className="w-4 h-4 text-stone-900" />
                        )}
                      </div>
                    
                    {/* Tooltip */}
                    <div className="absolute left-1/2 top-10 -translate-x-1/2 bg-white border border-stone-200 shadow-lg p-3 rounded min-w-[200px] opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <div className="flex items-start gap-3">
                        {hotspot.material?.thumbnail && (
                          <img 
                            src={hotspot.material.thumbnail} 
                            alt="" 
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-sm">{hotspot.label || hotspot.material?.name}</p>
                          <p className="text-xs text-stone-500">{hotspot.material?.stone_type}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteHotspot(hotspot.hotspot_id); }}
                          className="p-1 hover:bg-red-50 rounded text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                </div>

                {/* New Hotspot Preview */}
                {newHotspot && (
                  <div
                    className="absolute"
                    style={{ 
                      left: `${newHotspot.x_percent}%`, 
                      top: `${newHotspot.y_percent}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  >
                    <div className="w-8 h-8 bg-green-500 border-2 border-white rounded-full flex items-center justify-center shadow-lg animate-pulse">
                      <Plus className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}

                {/* Navigation */}
                {project.images?.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : project.images.length - 1)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev < project.images.length - 1 ? prev + 1 : 0)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/70 text-white text-sm rounded">
                      {currentImageIndex + 1} / {project.images.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {project.images?.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {project.images.map((img, idx) => {
                    const count = hotspots.filter(h => h.image_index === idx).length;
                    return (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={cn(
                          "relative flex-shrink-0 w-20 h-20 overflow-hidden rounded",
                          currentImageIndex === idx ? "ring-2 ring-stone-900" : "opacity-60 hover:opacity-100"
                        )}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        {count > 0 && (
                          <span className="absolute top-1 right-1 w-5 h-5 bg-stone-900 text-white text-xs rounded-full flex items-center justify-center">
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sidebar - Hotspots List */}
            <div className="space-y-4">
              <div className="bg-white border border-stone-200 p-4">
                <h2 className="font-medium text-stone-900 mb-4">
                  Hotspots ({hotspots.length})
                </h2>
                
                {hotspots.length === 0 ? (
                  <p className="text-sm text-stone-500 text-center py-8">
                    {editMode 
                      ? "Click on the image to add a hotspot"
                      : "No hotspots yet. Click 'Add Hotspots' to start."}
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {hotspots.map((hotspot, idx) => (
                      <div 
                        key={hotspot.hotspot_id || idx}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded transition-colors",
                          hotspot.image_index === currentImageIndex 
                            ? "bg-stone-100" 
                            : "hover:bg-stone-50"
                        )}
                      >
                        {hotspot.material?.thumbnail ? (
                          <img 
                            src={hotspot.material.thumbnail}
                            alt=""
                            className="w-10 h-10 object-cover rounded"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-stone-200 rounded" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {hotspot.label || hotspot.material?.name}
                          </p>
                          <p className="text-xs text-stone-500">
                            Image {hotspot.image_index + 1} • {hotspot.material?.stone_type}
                          </p>
                        </div>
                        <button
                          onClick={() => deleteHotspot(hotspot.hotspot_id)}
                          className="p-1.5 hover:bg-red-50 rounded text-stone-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Instructions */}
              {editMode && (
                <div className="bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                  <p className="font-medium mb-2">Edit Mode Active</p>
                  <ul className="list-disc list-inside space-y-1 text-green-700">
                    <li>Click anywhere on the image to place a hotspot</li>
                    <li>Select a material to link to the hotspot</li>
                    <li>Hover over existing hotspots to delete them</li>
                  </ul>
                </div>
              )}
              
              {dragMode && (
                <div className="bg-[var(--gold)]/10 border border-[var(--gold)]/30 p-4 text-sm text-white/80">
                  <p className="font-medium mb-2 text-[var(--gold)]">Reposition Mode Active</p>
                  <ul className="list-disc list-inside space-y-1 text-white/70">
                    <li>Click and drag any hotspot to reposition it</li>
                    <li>Release to save the new position</li>
                    <li>Click the button again to exit reposition mode</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Material Selection Dialog */}
      <Dialog open={showMaterialSearch} onOpenChange={(open) => {
        if (!open) cancelNewHotspot();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Material for Hotspot</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {newHotspot && (
              <p className="text-sm text-stone-500">
                Position: {newHotspot.x_percent.toFixed(1)}% x {newHotspot.y_percent.toFixed(1)}%
              </p>
            )}

            <div>
              <Label className="text-sm mb-2 block">Search Materials</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  value={materialSearch}
                  onChange={(e) => setMaterialSearch(e.target.value)}
                  placeholder="Search by name or type..."
                  className="pl-10"
                />
              </div>
            </div>

            <div className="max-h-[300px] overflow-y-auto space-y-1 border border-stone-200 rounded p-1">
              {filteredMaterials.length === 0 ? (
                <p className="text-sm text-stone-500 text-center py-4">No materials found</p>
              ) : (
                filteredMaterials.slice(0, 20).map((material) => (
                  <button
                    key={material.material_id}
                    onClick={() => setSelectedMaterial(material)}
                    className={cn(
                      "w-full flex items-center gap-3 p-2 rounded transition-colors text-left",
                      selectedMaterial?.material_id === material.material_id
                        ? "bg-stone-900 text-white"
                        : "hover:bg-stone-100"
                    )}
                  >
                    {material.images?.[0] ? (
                      <img 
                        src={material.images[0]} 
                        alt="" 
                        className="w-10 h-10 object-cover rounded"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-stone-200 rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{material.name}</p>
                      <p className={cn(
                        "text-xs",
                        selectedMaterial?.material_id === material.material_id
                          ? "text-stone-300"
                          : "text-stone-500"
                      )}>
                        {material.stone_type}
                      </p>
                    </div>
                    {selectedMaterial?.material_id === material.material_id && (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                ))
              )}
            </div>

            {selectedMaterial && (
              <div>
                <Label className="text-sm mb-2 block">Custom Label (optional)</Label>
                <Input
                  value={newHotspot?.label || ''}
                  onChange={(e) => setNewHotspot({ ...newHotspot, label: e.target.value })}
                  placeholder={selectedMaterial.name}
                />
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={cancelNewHotspot}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-stone-900 hover:bg-stone-800"
                onClick={saveHotspot}
                disabled={!selectedMaterial || saving}
              >
                {saving ? 'Saving...' : 'Add Hotspot'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HotspotEditor;
